import {
  createContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react'
import { useOktaAuth } from '@okta/okta-react'
import { type UserClaims } from '@okta/okta-auth-js'
import { jwtDecode } from 'jwt-decode'

export type ExtendedUserClaims = UserClaims & {
  customerNumber: number
}

// Custom claims Okta adds to the access token, on top of the standard JWT claims
type AccessTokenClaims = Record<string, unknown> & {
  klntnr?: string | number
}

export type UserContextValue = {
  user?: ExtendedUserClaims
  customerNumber?: number
  permittedScopes: string[]
  isAuthenticated: boolean
  isLoading: boolean
  hasScope: (scope: string) => boolean
}

const UserContext = createContext<UserContextValue | null>(null)

export const UserProvider = ({ children }: PropsWithChildren) => {
  const { oktaAuth, authState } = useOktaAuth()
  const [user, setUser] = useState<ExtendedUserClaims>()
  const [permittedScopes, setPermittedScopes] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const isAuthenticated = !!authState?.isAuthenticated

  useEffect(() => {
    if (!authState) {
      return
    }

    let isCancelled = false

    const loadUser = async () => {
      if (!authState.isAuthenticated) {
        if (!isCancelled) {
          setUser(undefined)
          setPermittedScopes([])
          setIsLoading(false)
        }
        return
      }

      const oktaUser = await oktaAuth.getUser()
      const claims = jwtDecode<AccessTokenClaims>(
        authState.accessToken?.accessToken as string
      )

      const customerNumber = Number(claims.klntnr)
      const scopes = Object.keys(claims).filter(
        claim => claims[claim] === 'true'
      )

      if (isCancelled) {
        return
      }

      setUser({ ...oktaUser, customerNumber })
      setPermittedScopes(scopes)
      setIsLoading(false)
    }

    loadUser().catch(() => {
      if (!isCancelled) {
        setUser(undefined)
        setPermittedScopes([])
        setIsLoading(false)
      }
    })

    return () => {
      isCancelled = true
    }
  }, [oktaAuth, authState])

  const value = useMemo<UserContextValue>(
    () => ({
      user,
      customerNumber: user?.customerNumber,
      permittedScopes,
      isAuthenticated,
      isLoading,
      hasScope: scope => permittedScopes.includes(scope),
    }),
    [user, permittedScopes, isAuthenticated, isLoading]
  )

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

export { UserContext }
